import { Loader2, MoreHorizontal, Search, ShieldCheck } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { Card, btnGhost, btnSecondary, inputBaseCls } from '../components/ui';
import { ApiError } from '../lib/api';
import { formatStamp } from '../lib/format';
import { useSession } from '../lib/session';

interface SiteUser {
  sub: string;
  email: string;
  status: string;
  enabled: boolean;
  createdAt?: string;
  name?: string;
  kind?: string;
  organization?: string;
  phone?: string;
  trusted: boolean;
  submissions: number;
  reports: number;
}

const KINDS: Record<string, string> = {
  persona: 'Privato',
  associazione: 'Associazione',
  comune: 'Comune',
  parrocchia: 'Parrocchia',
  azienda: 'Azienda',
  organizzatore: 'Organizzatore',
  media: 'Media',
  altro: 'Altro',
};

const errorText = (e: unknown) =>
  e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e);

/**
 * Utenti del sito (solo admin, docs/07-admin-e-bot.md "Utenti"): chi entra con email e codice
 * per segnalare eventi, chiedere correzioni, salvare i preferiti e rispondere ai sondaggi.
 * Ricerca per email, profilo e attività; fidato (le sue segnalazioni si pubblicano da sole se la
 * lettura è pulita), blocco dell'accesso, eliminazione dell'account.
 */
export function SiteUsers() {
  const { api } = useSession();
  const [users, setUsers] = useState<SiteUser[] | null>(null);
  const [cursor, setCursor] = useState<string | undefined>();
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = async (search: string, after?: string) => {
    setBusy(after ? 'more' : 'list');
    setError(null);
    try {
      const p = new URLSearchParams({
        ...(search ? { q: search } : {}),
        ...(after ? { cursor: after } : {}),
      });
      const r = await api<{ users: SiteUser[]; cursor?: string }>(`/site-users?${p}`);
      setUsers((prev) => (after && prev ? [...prev, ...r.users] : r.users));
      setCursor(r.cursor);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(null);
    }
  };
  useEffect(() => {
    void load('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const search = (ev: FormEvent) => {
    ev.preventDefault();
    setQuery(q.trim());
    void load(q.trim());
  };

  const update = (sub: string, patch: Partial<SiteUser>) =>
    setUsers((list) => list?.map((u) => (u.sub === sub ? { ...u, ...patch } : u)) ?? null);

  const run = async (u: SiteUser, fn: () => Promise<string>) => {
    setBusy(u.sub);
    setError(null);
    setNotice(null);
    try {
      setNotice(await fn());
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(null);
    }
  };

  const trust = (u: SiteUser) =>
    run(u, async () => {
      await api(`/imports/submitters/${u.sub}/trusted`, {
        method: 'PUT',
        body: { trusted: !u.trusted },
      });
      update(u.sub, { trusted: !u.trusted });
      return u.trusted
        ? `${u.email} non è più fidato: le sue segnalazioni passano dalla coda.`
        : `${u.email} è fidato: le sue segnalazioni si pubblicano da sole quando la lettura è pulita.`;
    });
  const toggle = (u: SiteUser) => {
    if (
      u.enabled &&
      !window.confirm(
        `Bloccare l’accesso di ${u.email}? Non potrà più entrare finché non lo sblocchi.`,
      )
    )
      return;
    void run(u, async () => {
      await api(`/site-users/${u.sub}/${u.enabled ? 'disable' : 'enable'}`, { method: 'POST' });
      update(u.sub, { enabled: !u.enabled });
      return u.enabled ? `Accesso di ${u.email} bloccato.` : `Accesso di ${u.email} sbloccato.`;
    });
  };
  const remove = (u: SiteUser) => {
    if (
      !window.confirm(
        `Eliminare l’account di ${u.email}? Segnalazioni e problemi restano senza i suoi contatti; preferiti, promemoria e profilo si cancellano. Non si può annullare.`,
      )
    )
      return;
    void run(u, async () => {
      await api(`/site-users/${u.sub}`, { method: 'DELETE' });
      setUsers((list) => list?.filter((x) => x.sub !== u.sub) ?? null);
      return `Account di ${u.email} eliminato.`;
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <p className="max-w-[70ch] text-sm text-muted">
        Chi entra sul sito con l’email e il codice: per segnalare eventi, chiedere correzioni o la
        promozione, salvare i preferiti e rispondere ai sondaggi. Le risposte ai sondaggi sono
        anonime e qui non compaiono.
      </p>
      <form onSubmit={search} className="flex flex-wrap gap-2">
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Inizio dell’email (es. mario)"
          aria-label="Cerca per email"
          className={`${inputBaseCls} w-[280px]`}
        />
        <button type="submit" className={btnSecondary}>
          <Search className="size-4" aria-hidden /> Cerca
        </button>
      </form>
      {error && (
        <p role="alert" className="bg-danger-soft p-3 text-sm">
          Errore: {error}
        </p>
      )}
      {notice && (
        <p role="status" className="bg-ok-soft p-3 text-sm">
          {notice}
        </p>
      )}
      <Card
        title={
          users
            ? `${query ? `Email che iniziano con “${query}”` : 'Iscritti'} (${users.length}${cursor ? '+' : ''})`
            : 'Iscritti'
        }
      >
        {!users ? (
          <p className="text-sm text-muted">Caricamento…</p>
        ) : users.length === 0 ? (
          <p className="text-sm text-muted">Nessuno.</p>
        ) : (
          <ul className="flex flex-col divide-y divide-surface-strong">
            {users.map((u) => (
              <li key={u.sub} className="flex flex-wrap items-center gap-3 py-3 text-sm">
                <span className="min-w-[240px] flex-1">
                  {u.name && <b>{u.name} · </b>}
                  {u.email}
                  <span className="block text-xs text-muted">
                    {[
                      u.kind && (KINDS[u.kind] ?? u.kind),
                      u.organization,
                      u.phone,
                      u.createdAt && `iscritto ${formatStamp(u.createdAt)}`,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </span>
                <span className="text-xs text-muted">
                  {u.submissions} {u.submissions === 1 ? 'segnalazione' : 'segnalazioni'} ·{' '}
                  {u.reports} {u.reports === 1 ? 'richiesta' : 'richieste'}
                </span>
                {u.trusted && (
                  <span className="inline-flex items-center gap-1 bg-ok-soft px-2 py-0.5 text-xs">
                    <ShieldCheck className="size-3.5" aria-hidden /> Fidato
                  </span>
                )}
                {!u.enabled && (
                  <span className="bg-surface-strong px-2 py-0.5 text-xs text-muted">Bloccato</span>
                )}
                <details className="relative">
                  <summary
                    className={`${btnGhost} cursor-pointer list-none`}
                    aria-label={`Azioni per ${u.email}`}
                  >
                    {busy === u.sub ? (
                      <Loader2 className="size-4 animate-spin" aria-hidden />
                    ) : (
                      <MoreHorizontal className="size-4" aria-hidden />
                    )}
                  </summary>
                  <div className="absolute right-0 z-10 mt-1 flex w-64 flex-col border border-surface-strong bg-page py-1 shadow-lg">
                    <Item onClick={() => void trust(u)}>
                      {u.trusted ? 'Togli “fidato”' : 'Segna come fidato'}
                    </Item>
                    <Item onClick={() => toggle(u)}>
                      {u.enabled ? 'Blocca l’accesso' : 'Sblocca l’accesso'}
                    </Item>
                    <Item danger onClick={() => remove(u)}>
                      Elimina l’account
                    </Item>
                  </div>
                </details>
              </li>
            ))}
          </ul>
        )}
        {cursor && (
          <button
            type="button"
            className={`${btnSecondary} self-start`}
            disabled={busy === 'more'}
            onClick={() => void load(query, cursor)}
          >
            {busy === 'more' && <Loader2 className="size-4 animate-spin" aria-hidden />}
            Mostra altri
          </button>
        )}
      </Card>
    </div>
  );
}

function Item({
  onClick,
  danger,
  children,
}: {
  onClick: () => void;
  danger?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={(e) => {
        (e.currentTarget.closest('details') as HTMLDetailsElement | null)?.removeAttribute('open');
        onClick();
      }}
      className={`px-3 py-2 text-left text-sm hover:bg-surface ${danger ? 'text-danger' : ''}`}
    >
      {children}
    </button>
  );
}
